/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  const DEBUG_VARIANT_PICKER = false; // 调试：选项闪退时设为 true，排查后可关

  // ──────────────────────────────────────────────────────────────────────────
  // 高变体产品（>250）加购拦截器
  // 该类产品的真正加购可能由第三方 app（Wizz / Globo）或原生表单发出，绕过
  // 主题的 handleSubmit，导致提交的是客户端兜底的"默认变体 id"。这里在网络层
  // 统一拦截所有 /cart/add 请求，把请求体里的变体 id 替换为服务器端按用户实际
  // 选择解析出的正确变体 id。只有当请求里的 id 确属本产品的变体时才替换，避免
  // 影响页面上其它产品的加购。任何异常都安全回退到原始请求，绝不阻断加购。
  // ──────────────────────────────────────────────────────────────────────────
  const HV_PICKERS = new Set();
  let HV_PATCHED = false;

  const hvIsCartAdd = (url) => typeof url === 'string' && url.indexOf('/cart/add') !== -1;

  // 诊断：记录每一次观察到的 /cart/add，便于排查加购走哪条路径、带什么 id。
  function hvRecord(via, url, body) {
    try {
      let bodyType = body == null ? 'null' : (body.constructor && body.constructor.name) || typeof body;
      let cur = null;
      if (typeof FormData !== 'undefined' && body instanceof FormData) cur = body.get('id');
      else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) cur = body.get('id');
      else if (typeof body === 'string') {
        try { const j = JSON.parse(body); cur = j && (j.id != null ? j.id : (Array.isArray(j.items) && j.items[0] ? j.items[0].id : null)); }
        catch (e) { try { cur = new URLSearchParams(body).get('id'); } catch (e2) {} }
      }
      window.__hvCartAdds = window.__hvCartAdds || [];
      window.__hvCartAdds.push({ via, url, bodyType, currentId: cur, at: new Date().toISOString() });
      console.log('🟢 [HV观察]', via, url, 'bodyType=', bodyType, 'id=', cur);
    } catch (e) {}
  }

  // 找到"拥有该 currentId 变体"的高变体 picker，解析并返回正确变体 id。
  // currentId 必须命中某个 picker 的变体集合，否则返回 null（不替换，防误伤）。
  async function hvResolveReplacementId(currentId) {
    if (currentId == null || currentId === '') return null;
    for (const p of HV_PICKERS) {
      if (!p.isHighVariant || !p.data || !p.data.variants) continue;
      const ids = p.data.variants.map((v) => String(v.id));
      if (ids.indexOf(String(currentId)) === -1) continue;
      try { await p.ensureResolvedVariantId(); } catch (e) { /* 安全回退 */ }
      if (p._resolvedId && String(p._resolvedId) !== String(currentId)) {
        try { window.__hvResolvedId = String(p._resolvedId); } catch (e) {}
        return String(p._resolvedId);
      }
      return null;
    }
    return null;
  }

  // 解析并改写请求体里的变体 id。返回 { body, currentId, newId } 或 null（不改）。
  async function hvRewriteBody(body) {
    if (body == null) return null;

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const cur = body.get('id');
      const newId = await hvResolveReplacementId(cur);
      if (!newId) return null;
      body.set('id', newId);
      return { body, currentId: cur, newId };
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      const cur = body.get('id');
      const newId = await hvResolveReplacementId(cur);
      if (!newId) return null;
      body.set('id', newId);
      return { body, currentId: cur, newId };
    }

    if (typeof body === 'string') {
      let json = null;
      try { json = JSON.parse(body); } catch (e) { json = null; }
      if (json && typeof json === 'object') {
        let cur = null;
        if (Object.prototype.hasOwnProperty.call(json, 'id')) cur = json.id;
        else if (Array.isArray(json.items) && json.items.length) cur = json.items[0].id;
        const newId = await hvResolveReplacementId(cur);
        if (!newId) return null;
        const numId = Number(newId);
        if (Object.prototype.hasOwnProperty.call(json, 'id')) json.id = numId;
        if (Array.isArray(json.items)) {
          json.items.forEach((it) => { if (it && it.id != null) it.id = numId; });
        }
        return { body: JSON.stringify(json), currentId: cur, newId };
      }
      const params = new URLSearchParams(body);
      if (params.has('id')) {
        const cur = params.get('id');
        const newId = await hvResolveReplacementId(cur);
        if (!newId) return null;
        params.set('id', newId);
        return { body: params.toString(), currentId: cur, newId };
      }
      return null;
    }

    return null;
  }

  function hvPatchCartAdd() {
    if (HV_PATCHED) return;
    HV_PATCHED = true;

    // 1) fetch
    const origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = async function (input, init) {
        try {
          const url = typeof input === 'string' ? input : (input && input.url) || '';
          if (hvIsCartAdd(url) && HV_PICKERS.size) {
            // 仅处理 init.body（覆盖绝大多数 app）。Request 对象 body 为流，无法
            // 同步读取，遇到时安全放行。
            let body = init && init.body;
            if (body == null && typeof input !== 'string' && input && 'body' in input) {
              body = null; // Request 流，放行
            }
            hvRecord('fetch', url, body);
            if (body != null) {
              const res = await hvRewriteBody(body);
              if (res) {
                console.log('🟢 [HV拦截 fetch] /cart/add 变体 id', res.currentId, '→', res.newId);
                const newInit = Object.assign({}, init, { body: res.body });
                return origFetch.call(this, input, newInit);
              }
            }
          }
        } catch (e) { /* 安全回退 */ }
        return origFetch.apply(this, arguments);
      };
    }

    // 2) XMLHttpRequest
    const OrigOpen = XMLHttpRequest.prototype.open;
    const OrigSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__hvUrl = url;
      return OrigOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (hvIsCartAdd(this.__hvUrl) && HV_PICKERS.size) {
          hvRecord('xhr', this.__hvUrl, body);
        }
        if (hvIsCartAdd(this.__hvUrl) && HV_PICKERS.size && body != null) {
          const xhr = this;
          hvRewriteBody(body).then((res) => {
            if (res) {
              console.log('🟢 [HV拦截 XHR] /cart/add 变体 id', res.currentId, '→', res.newId);
              OrigSend.call(xhr, res.body);
            } else {
              OrigSend.call(xhr, body);
            }
          }).catch(() => OrigSend.call(xhr, body));
          return;
        }
      } catch (e) { /* 安全回退 */ }
      return OrigSend.apply(this, arguments);
    };
  }

  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // 高变体产品（>250）：Shopify 的 Liquid / .js 最多只暴露前 250 个变体，
      // 所以前端的变体表是"不完整"的，靠它匹配会找不到真变体而错乱。
      // 标记后，在加购提交时用服务器端解析正确的变体 id（见 ensureResolvedVariantId）。
      this.isHighVariant = this.data.variants.length >= 250;

      if (DEBUG_VARIANT_PICKER) {
        console.log('[VariantPicker DEBUG] constructor: optionSelectors=', this.optionSelectors.length, 'variants=', this.data.variants.length, 'selectedOptions=', this.selectedOptions, 'variant=', this.variant ? this.variant.title : null);
      }

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.applySearchParams();

      // 高变体产品：颜色 UI 可能是 Globo（在 variant-picker 之外），其交互不会触发
      // 本组件的 change 事件。因此在整个 section 上监听 click/change，确保用户用
      // Globo 选色后也会触发变体解析。
      if (this.isHighVariant && this.section) {
        const onSectionInteract = () => this.scheduleResolveVariant();
        this.section.addEventListener('click', onSectionInteract, true);
        this.section.addEventListener('change', onSectionInteract, true);

        // 注册到加购拦截器：无论真正加购走哪条路径（Wizz / 原生 / Globo），
        // 都会在网络层把变体 id 改成解析出的正确 id。
        HV_PICKERS.add(this);
        hvPatchCartAdd();
      }

      this.setAttribute('loaded', '');
      // Re-run availability after custom-select/dropdown has set initial selection (prevents options flashing then disappearing)
      setTimeout(() => {
        this.selectedOptions = this.getSelectedOptions();
        this.variant = this.getSelectedVariant();
        if (DEBUG_VARIANT_PICKER) {
          console.log('[VariantPicker DEBUG] after 150ms: selectedOptions=', this.selectedOptions, 'variant=', this.variant ? this.variant.title : null);
        }
        this.updateAvailability();
        this.updateAddToCartButton();
      }, 150);

      if (DEBUG_VARIANT_PICKER) {
        this._debugObserveVisibility();
      }
    }

    _debugObserveVisibility() {
      const el = this;
      const log = (reason, target, key, value) => {
        console.warn('[VariantPicker DEBUG] 可能导致选项消失:', reason, target?.className || target?.tagName, key, value, new Error().stack?.split('\n').slice(2, 5).join(' <- '));
      };
      const check = (node) => {
        if (!node || !node.getAttribute) return;
        const d = node.style?.display ?? window.getComputedStyle(node).display;
        const v = node.style?.visibility ?? window.getComputedStyle(node).visibility;
        const h = node.getAttribute('hidden');
        if (d === 'none' || v === 'hidden' || h !== null) {
          log('display/visibility/hidden', node, { display: d, visibility: v, hidden: h }, '');
        }
      };
      const obs = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          if (m.type === 'attributes' && (m.attributeName === 'style' || m.attributeName === 'hidden' || m.attributeName === 'class')) {
            check(m.target);
            if (m.target === el || el.contains(m.target)) {
              const style = m.target.getAttribute('style') || '';
              if (style.includes('display') || style.includes('visibility') || m.target.getAttribute('hidden') !== null) {
                log('attribute change on picker or child', m.target, m.attributeName, m.target.getAttribute(m.attributeName));
              }
            }
          }
          if (m.type === 'childList' && m.removedNodes.length) {
            m.removedNodes.forEach((n) => {
              if (n.nodeType === 1 && (n === el || el.contains(n))) {
                log('child removed from picker tree', n, 'removed', '');
              }
            });
          }
        });
      });
      obs.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['style', 'hidden', 'class'], childList: true });
      setTimeout(() => {
        check(el);
        check(el.parentElement);
        check(el.closest('.input-row'));
        const visible = el.offsetParent !== null && window.getComputedStyle(el).display !== 'none';
        console.log('[VariantPicker DEBUG] 当前 picker 可见?', visible, 'offsetParent=', el.offsetParent, 'display=', window.getComputedStyle(el).display);
      }, 50);
      setTimeout(() => {
        obs.disconnect();
      }, 8000);
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // If selection contains a URL for different product
      const productUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      if (productUrl) {
        // Check for product change
        const selectedUrl = new URL(productUrl, window.location.origin);
        if (selectedUrl.pathname !== window.location.pathname) {
          // Preserve options
          const params = `option_values=${this.getSelectedOptionIds().join(',')}`;
          const separator = productUrl.includes('?') ? '&' : '?';
          window.location = `${productUrl}${separator}${params}`;
          return;
        }
      }

      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));

      // 高变体产品：选完颜色后立刻在服务器端解析正确变体并写回 id（去抖），
      // 这样无论走哪条加购提交路径，提交的都是正确变体 id。
      this.scheduleResolveVariant();
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      
      // 检查是否是允许任意颜色组合的产品（AccoGift, MuseStand, Arena）
      const productTitle = document.querySelector('.product-title')?.textContent || '';
      const isCustomizableProduct = productTitle.includes('AccoGift') || 
                                     productTitle.includes('MuseStand') || 
                                     productTitle.includes('Arena');
      
      console.log('📝 [DEBUG VariantPicker] 产品标题:', productTitle);
      console.log('📝 [DEBUG VariantPicker] 是否可自定义产品:', isCustomizableProduct);
      console.log('📝 [DEBUG VariantPicker] 当前variant:', this.variant);
      
      if (isCustomizableProduct) {
        // 可自定义产品：只要有任何变体存在就允许购买
        const anyVariantAvailable = this.data.variants.some(v => v.available);
        if (anyVariantAvailable) {
          // 没有"完全匹配"的变体时，退回到"与用户实际选择最接近"的可售变体，
          // 让订单上的变体标题尽量贴合用户的真实选择（真实选择仍以 properties 为准）。
          if (!this.variant) {
            this.variant = this.getClosestAvailableVariant() || this.data.variants.find(v => v.available);
            // 更新隐藏的变体ID输入
            this.updateVariantInput();
          }
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
          
          // 保存选中的颜色为 line item properties
          this.saveSelectedColorsAsProperties();
          return;
        }
      }
      
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }
    
    /**
     * Saves selected color options as hidden line item properties for AccoGift products.
     */
    saveSelectedColorsAsProperties() {
      if (!this.productForm) return;
      
      // 移除旧的颜色属性输入
      this.productForm.querySelectorAll('.accogift-color-property').forEach(el => el.remove());
      
      // 为每个选项创建隐藏输入
      this.optionSelectors.forEach((selector, index) => {
        const optionName = selector.dataset.option || `Option ${index + 1}`;
        const optionValue = this.selectedOptions[index];
        
        if (optionValue) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${optionName}]`;
          input.value = optionValue;
          input.classList.add('accogift-color-property');
          this.productForm.appendChild(input);
        }
      });
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'off') return;

      this.optionSelectors.forEach((selector, selectorIndex) => {
        const options = selector.querySelectorAll('.js-option:not([data-value=""])');
        let optionThatExists = false;
        let nonexistentCount = 0;
        options.forEach((option) => {
          // Skip combined listings
          if (option.dataset.productUrl) return;

          const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
          // Normalize option value for comparison (trim whitespace and convert to lowercase for case-insensitive comparison)
          const normalizedOptionValue = optionValue ? String(optionValue).trim().toLowerCase() : optionValue;
          
          // any available variants with previous options and this one locked in?
          let variantsExist = false;
          let variantsAvailable = false;
          this.data.variants.forEach((v) => {
            let matches = 0;
            for (let i = 0; i < selectorIndex; i += 1) {
              const variantOption = v.options[i] ? String(v.options[i]).trim().toLowerCase() : v.options[i];
              const selectedOption = this.selectedOptions[i] ? String(this.selectedOptions[i]).trim().toLowerCase() : this.selectedOptions[i];
              if (variantOption === selectedOption || this.selectedOptions[i] === null) {
                matches += 1;
              }
            }
            const variantOptionValue = v.options[selectorIndex] ? String(v.options[selectorIndex]).trim().toLowerCase() : v.options[selectorIndex];
            if (variantOptionValue === normalizedOptionValue && matches === selectorIndex) {
              variantsExist = true;
              if (!optionThatExists) {
                optionThatExists = option;
              }
              if (v.available) {
                variantsAvailable = true;
              }
            }
          });
          
          VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          if (option.classList.contains('is-nonexistent')) nonexistentCount++;
        });
        if (DEBUG_VARIANT_PICKER) {
          console.log('[VariantPicker DEBUG] updateAvailability selectorIndex=', selectorIndex, 'options=', options.length, 'nonexistent=', nonexistentCount, 'availability=', this.dataset.availability);
        }
        // Prevent all options from being hidden: if every option is is-nonexistent (e.g. matching bug), keep them visible
        if (this.dataset.availability === 'prune') {
          const allNonexistent = options.length > 0 && Array.from(options).every((opt) => opt.classList.contains('is-nonexistent'));
          if (allNonexistent) {
            if (DEBUG_VARIANT_PICKER) console.log('[VariantPicker DEBUG] prune: 全部 is-nonexistent，已移除 class 保持显示');
            options.forEach((opt) => {
              opt.classList.remove('is-nonexistent', 'is-unavailable');
            });
          } else if (optionThatExists && selector.querySelector('.js-option.is-nonexistent:is(:checked, [aria-selected="true"])')) {
            setTimeout(() => {
              if (optionThatExists.closest('custom-select')) {
                optionThatExists.closest('custom-select').selectOption(optionThatExists);
              } else {
                optionThatExists.click();
              }
            }, 0);
          }
        }
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price .price');
      if (!this.price) return;

      let { variant } = this;
      
      // 检查是否是可自定义产品（AccoGift, MuseStand, Arena）
      const productTitle = document.querySelector('.product-title')?.textContent || '';
      const isCustomizableProduct = productTitle.includes('AccoGift') || 
                                     productTitle.includes('MuseStand') || 
                                     productTitle.includes('Arena');
      
      // 可自定义产品：如果没有匹配变体，使用第一个可用变体来显示价格
      if (isCustomizableProduct && !variant) {
        variant = this.data.variants.find(v => v.available) || this.data.variants[0];
      }
      
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update price.
        if (variant.compare_at_price > variant.price) {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
        } else {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          priceWasEl.querySelector('.js-label').textContent = '';
          priceWasEl.querySelector('.js-value').innerHTML = '';
        }

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection && !isCustomizableProduct);
      }

      // 可自定义产品：始终显示价格，隐藏 "no variant" 提示
      const showPrice = this.variant || this.preSelection || isCustomizableProduct;
      this.price.querySelector('.price__default').hidden = !showPrice;
      this.price.querySelector('.price__no-variant').hidden = showPrice;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => {
          const variantOption = val ? String(val).trim().toLowerCase() : val;
          const selectedOption = this.selectedOptions[index] ? String(this.selectedOptions[index]).trim().toLowerCase() : this.selectedOptions[index];
          return variantOption === selectedOption;
        })
      );
    }

    /**
     * 当用户选择的颜色组合没有对应的真实变体时，从可售变体里挑一个
     * 与用户实际选择"逐项匹配数最多"的变体，让订单上的变体标题尽量贴合
     * 用户的真实选择（真实选择本身仍以 line item properties 为准）。
     * @returns {?object} 最接近的可售变体，没有可售变体时返回 null。
     */
    getClosestAvailableVariant() {
      const norm = (val) => (val ? String(val).trim().toLowerCase() : val);
      const selected = this.selectedOptions.map(norm);

      let best = null;
      let bestScore = -1;

      this.data.variants.forEach((v) => {
        if (!v.available) return;
        let score = 0;
        v.options.forEach((val, index) => {
          if (selected[index] != null && norm(val) === selected[index]) {
            score += 1;
          }
        });
        if (score > bestScore) {
          bestScore = score;
          best = v;
        }
      });

      return best;
    }

    /**
     * 取得本 section 的 id（用于 Section Rendering API）。
     * @returns {?string}
     */
    getSectionId() {
      const sectionWrap = this.closest('.shopify-section');
      if (sectionWrap && sectionWrap.id) {
        return sectionWrap.id.replace('shopify-section-', '');
      }
      return (this.section && this.section.dataset && this.section.dataset.section) || null;
    }

    /**
     * 高变体产品（>250）专用：在加购提交前，用 Shopify 的 Section Rendering API
     * 按"用户选中的选项值 id"在服务器端解析出真正对应的变体 id，并写回表单的
     * [name="id"]。Shopify 服务器端不受 250 限制，因此能命中全部组合，
     * 让订单/确认邮件显示的变体与用户实际选择一致。
     * 任何失败都会安静地保留现有 id（兜底），不阻塞加购。
     * @returns {Promise<void>}
     */
    /**
     * 取得"实际选中"的 option value id 列表，按选项顺序排列。
     * 该产品的颜色 UI 可能是 Globo（写入 properties[...]）而非原生单选框，
     * 因此优先用原生单选框；缺失时回退到从 properties 值反查 value id，
     * 保证无论哪套 UI 都能拿到正确的选项。
     * @returns {Array<?string>}
     */
    /**
     * 在整个文档里查找某个选项名对应的、用户真实选择的属性值。
     * Globo 等第三方 app 的 properties 输入是运行时注入的，且可能不在主题表单内，
     * 因此全文档范围匹配 name="properties[<optionName>]"（忽略大小写与空格）。
     * @param {string} optionName
     * @returns {?string}
     */
    findPropertyValue(optionName) {
      if (!optionName) return null;
      const target = optionName.trim().toLowerCase();
      const inputs = document.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
      for (const el of inputs) {
        const m = /^properties\[(.+)\]$/.exec(el.name || '');
        if (!m) continue;
        if (m[1].trim().toLowerCase() !== target) continue;
        // 单选/复选只取选中的
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) continue;
        if (el.value && el.value.trim()) return el.value;
      }
      return null;
    }

    getEffectiveOptionValueIds() {
      const native = this.getSelectedOptionIds();
      const norm = (v) => (v == null ? '' : String(v).trim().toLowerCase());
      const diag = [];

      const ids = Array.from(this.optionSelectors).map((selector, idx) => {
        const optionName = selector.dataset.option;
        const candidates = selector.querySelectorAll('input.js-option, .custom-select__option');
        const idFromName = (valueName) => {
          if (!valueName) return null;
          const match = Array.from(candidates).find((el) => {
            const v = el.value !== undefined && el.value !== '' ? el.value : el.dataset.value;
            return norm(v) === norm(valueName);
          });
          return match ? match.dataset.valueId : null;
        };

        // 收集"用户选中的值名"的多个可能来源，按可靠度排序。
        // 该产品的颜色 UI 是 Globo（运行时注入），原生单选框可能停在默认值。
        const sources = [];
        // a) Globo 选中色块（MuseStand 证实可读到正确颜色名）
        const globo = document.querySelector(`input[name="globo-option-${idx}"]:checked`);
        if (globo && globo.value) sources.push(['globo', globo.value]);
        // b) Globo 注入的 properties（加购时才有，全文档查找）
        const propVal = this.findPropertyValue(optionName);
        if (propVal) sources.push(['prop', propVal]);
        // c) text-option / 通用选中态
        const textSel = document.querySelector(`.text-option[data-index="${idx}"].selected, .selected[data-index="${idx}"]`);
        if (textSel && (textSel.dataset.value || textSel.textContent)) {
          sources.push(['text', textSel.dataset.value || textSel.textContent.trim()]);
        }

        // 用第一个能映射成 value id 的来源
        for (const [src, val] of sources) {
          const mapped = idFromName(val);
          if (mapped) {
            diag.push(`${optionName}: [${src}] "${val}" → ${mapped}`);
            return mapped;
          }
        }

        // 兜底：原生选中状态
        let nativeId = null;
        let nativeVal = null;
        if (selector.dataset.selectorType === 'dropdown') {
          const sel = selector.querySelector('.custom-select__option[aria-selected="true"]');
          if (sel) { nativeId = sel.dataset.valueId; nativeVal = sel.dataset.value; }
        } else {
          const checked = selector.querySelector('input:checked');
          if (checked) { nativeId = checked.dataset.valueId; nativeVal = checked.value; }
        }
        nativeId = nativeId || native[idx] || null;
        diag.push(`${optionName}: 来源=${JSON.stringify(sources)}(均未映射) → 兜底原生 "${nativeVal}" ${nativeId}`);
        return nativeId;
      });

      console.log('🧩 [HighVariant] 选项来源诊断:', diag, 'ids=', ids);
      try { window.__hvLast = { diag, ids, at: new Date().toISOString() }; } catch (e) {}
      return ids;
    }

    async ensureResolvedVariantId() {
      if (!this.isHighVariant) return;

      const optionIds = this.getEffectiveOptionValueIds();
      // 选项未选全时不处理，交给表单原本的校验
      if (!optionIds.length || optionIds.some((id) => !id)) {
        console.log('🧩 [HighVariant] 选项未选全（含 Globo 回退后仍缺），跳过解析', optionIds);
        return;
      }

      const sectionId = this.getSectionId();
      if (!sectionId) {
        console.warn('🧩 [HighVariant] 未取到 sectionId，无法解析');
        return;
      }

      const sig = optionIds.join(',');
      // 已解析过相同组合则直接复用，避免重复请求
      if (this._resolvedSig === sig && this._resolvedId) {
        this.applyResolvedVariantId(this._resolvedId);
        return;
      }

      try {
        const url = `${this.dataset.url}?section_id=${encodeURIComponent(sectionId)}&option_values=${sig}`;
        console.log('🧩 [HighVariant] 解析时认为选中的颜色:', this.getSelectedOptions(), '→ option_value ids:', optionIds);
        console.log('🧩 [HighVariant] 解析变体中:', url);
        const res = await fetch(url, { cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) {
          console.warn('🧩 [HighVariant] 解析请求失败:', res.status);
          return;
        }

        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const allIds = Array.from(doc.querySelectorAll('[name="id"]'))
          .map((el) => el.value)
          .filter((v) => v);

        // 返回的 section 里可能有多个 name="id"：第一个常是产品的"默认/首个可售
        // 变体"（混入的其它表单），真正按 option_values 解析出的变体往往出现在
        // 主表单且重复多次。因此跳过默认变体，取出现次数最多的那个非默认 id。
        const defaultVariant = this.data.variants.find((v) => v.available) || this.data.variants[0];
        const defaultId = defaultVariant ? String(defaultVariant.id) : null;
        const counts = {};
        allIds.forEach((v) => { counts[v] = (counts[v] || 0) + 1; });
        let resolvedId = null;
        let bestCount = 0;
        Object.keys(counts).forEach((v) => {
          if (v === defaultId) return;
          if (counts[v] > bestCount) { bestCount = counts[v]; resolvedId = v; }
        });
        // 若没有任何非默认 id（说明该组合确实对应默认变体），回退到第一个。
        if (!resolvedId) resolvedId = allIds[0] || null;

        try {
          window.__hvResolve = { url, status: res.status, sig, resolvedId, defaultId, allIds, counts, len: text.length, at: new Date().toISOString() };
        } catch (e) {}
        console.log('🧩 [HighVariant] 返回里所有 name="id":', allIds, '默认=', defaultId, '→ 选定=', resolvedId);
        if (!resolvedId) {
          console.warn('🧩 [HighVariant] 返回的 HTML 里没有有效的变体 id，保留现有 id');
          return; // 该组合无对应变体时保留现有 id（含重复属性作兜底）
        }

        this._resolvedSig = sig;
        this._resolvedId = resolvedId;
        console.log('🧩 [HighVariant] 解析到正确变体 id:', resolvedId);
        this.applyResolvedVariantId(resolvedId);
      } catch (e) {
        // 网络/解析失败 → 保留现有（最接近匹配）的变体 id，不影响加购
        console.warn('🧩 [HighVariant] 解析异常，保留现有 id:', e);
      }
    }

    /**
     * 把解析出的正确变体 id 写回所有表单的 [name="id"]，并移除重复的颜色属性。
     * @param {string} resolvedId
     */
    applyResolvedVariantId(resolvedId) {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');
      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        if (input) input.value = resolvedId;
      });
      // 注意：暂不移除重复颜色属性，先确保 properties 始终保留真实选择作为兜底，
      // 避免"解析回落到默认 + 属性被删"导致信息全丢。等变体解析确认稳定后再启用去重。
    }

    /**
     * 去抖触发变体解析（选项变化时调用）。
     */
    scheduleResolveVariant() {
      if (!this.isHighVariant) return;
      clearTimeout(this._resolveTimer);
      this._resolveTimer = setTimeout(() => {
        this.ensureResolvedVariantId();
      }, 250);
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
